import { useEffect, useState } from "react";

import {
  ArrowButton,
  Button,
  DatePicker,
  InfoPanel,
  Label,
  Table,
  TableMetadata,
  useTable,
  Typography,
  ColumnSortEnum,
  TextField,
  Select,
  TableStateWithFilters,
  IconWrapper,
  SelectEvent
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { ReactComponent as FolderAddIcon } from "@king-ict/eupisi-ui/components/Icon/FolderAdd.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { ExportTypeEnum, PagedResult } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, hrDate, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  searchProgramRequestsInstitution, getAndCheckInstitutionRequest, SearchProgramRequestsInstitutionRequest,
  ExportProgramRequestInstitutionRequest, exportProgramRequestInstitution, getUnlockRequestCount, getUserInstitutionSession
} from "../../api/nisooApi"
import InstitutionBasicInfo from "../../components/InstitutionBasicInfo";
import { useDidUpdateEffect, useInput } from "../../hooks";
import { Institution, OIBServiceCheckStatus } from "../../models/Institution";
import {
  ProgramRequestListItem,
  ProgramRequestInstitutionSearch,
  ProgramRequestSort,
  ProgramRequestInstitutionSearchValidationScheme, PreviousPage
} from "../../models/ProgramRequest";
import { SortDirection } from "../../models/Shared/SortDirection";
import { RoleEnum } from "../../models/User/RoleEnum";
import { useGetProgramRequestStatusesQuery, useGetProgramTypesQuery, useGetUserInstitutionsQuery } from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { programRequestNewRoute, programRequestDetailsUrl, institutionEditRoute, createGroupProgramRequest } from "../../routes/routes";
import { getProgramRequestStatusColor } from "../../utils/programRequestUtils";
import styles from "./Dashboard.module.scss";

export function Dashboard() {
  const storageKey = 'DashboardSearchTableState';
  const infoStorageKey = 'DashboardInfoPanel';
  const infoPanelState = sessionStorage.getItem(infoStorageKey);

  const createInitialTableState = (): TableStateWithFilters<ProgramRequestInstitutionSearch> => {
    const sessionStorageTableState = sessionStorage.getItem(storageKey);
    if (sessionStorageTableState === null) {
      return {
        programTypeIds: [],
        programName: "",
        programRequestStatusIds: [],
        dateFrom: undefined,
        dateTo: undefined,
        classificationMark: "",
        page: 1,
        pageSize: 10,
        sortBy: ProgramRequestSort.ProgramRequestDate,
        order: ColumnSortEnum.DESC
      };
    }

    var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<ProgramRequestInstitutionSearch>;
    //JSON parse cannot parse dates - it reads the dates as string - so we do it manually
    if (result.dateFrom) {
      result.dateFrom = new Date(result.dateFrom.toString());
    }
    if (result.dateTo) {
      result.dateTo = new Date(result.dateTo.toString());
    }
    return result;
  }

  const createInitialProgramRequestSearch = (initialTableState: TableStateWithFilters<ProgramRequestInstitutionSearch>): ProgramRequestInstitutionSearch => {
    return {
      programTypeIds: initialTableState.programTypeIds,
      programName: initialTableState.programName,
      programRequestStatusIds: initialTableState.programRequestStatusIds,
      dateFrom: initialTableState.dateFrom,
      dateTo: initialTableState.dateTo,
      classificationMark: initialTableState.classificationMark
    }
  }

  const createInitialProgramRequests = (): PagedResult<ProgramRequestListItem> => {
    return {
      currentPage: 1,
      firstRowOnPage: 0,
      lastRowOnPage: 0,
      pageCount: 0,
      pageSize: 10,
      rowCount: 0,
      results: [],
    }
  }

  const [initialTableState] = useState<TableStateWithFilters<ProgramRequestInstitutionSearch>>(() => createInitialTableState());
  const [userHasProgramRequests, setUserHasProgramRequests] = useState<boolean>(false);
  const [userHasUnlockaedInit, setUserHasUnlockaedInit] = useState<boolean>(false);
  const [userHasUnlockaedRequests, setUserHasUnlockaedRequests] = useState<boolean>(false);
  const [programRequests, setProgramRequests] = useState<PagedResult<ProgramRequestListItem>>(() => createInitialProgramRequests());
  const [programRequestSearch, setProgramRequestSearch] = useState<ProgramRequestInstitutionSearch>(() => createInitialProgramRequestSearch(initialTableState));
  const [lastUsedSearch, setLastUsedSearch] = useState<SearchProgramRequestsInstitutionRequest>();
  const [institutionData, setInstitutionData] = useState<Institution | undefined>(undefined);
  const { data: userSession } = useGetUserSessionQuery();
  const { data: userInstitutions } = useGetUserInstitutionsQuery(userSession?.userId!, {
    skip: !userSession || (userSession.roleId !== RoleEnum.InstitutionAdmin && userSession.roleId !== RoleEnum.InstitutionDirector),
  });
  const [selectedInstitution, setSelectedInstitution] = useState(userSession?.institutionId!);

  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const { data: programTypes } = useGetProgramTypesQuery();
  const { data: programRequestStatuses } = useGetProgramRequestStatusesQuery();

  const [errors, doValidations] = useValidation({
    data: programRequestSearch,
    validationSchema: ProgramRequestInstitutionSearchValidationScheme,
  });

  if (userSession?.institutionId && !selectedInstitution) {
    setSelectedInstitution(userSession?.institutionId);
  }

  const programRequestTableMetadata: TableMetadata<ProgramRequestListItem> = {
    rowKey: "programRequestId",
    columns: [
      {
        columnKey: "programRequestDate",
        header: t("common.programRequestDate"),
        accessor: (item: ProgramRequestListItem) =>
          hrDate(item.programRequestDate) + ' ' +
          item.programRequestDate.getHours().toString().padStart(2, '0') + ':' +
          item.programRequestDate.getMinutes().toString().padStart(2, '0'),
        sortBy: ProgramRequestSort.ProgramRequestDate
      },
      {
        columnKey: "programName",
        header: t("common.programName"),
        accessor: "programName",
        sortBy: ProgramRequestSort.ProgramName
      },
      {
        columnKey: "programTypeName",
        header: t("common.programType"),
        accessor: "programTypeName",
        sortBy: ProgramRequestSort.ProgramTypeName
      },
      {
        columnKey: "classificationMark",
        header: t("common.classificationMark"),
        accessor: "classificationMark",
        sortBy: ProgramRequestSort.ClassificationMark
      },
      {
        columnKey: "programRequestStatusName",
        header: t("common.status"),
        accessor: (item) => {
          return (
            <div className={styles.statusColumn}>
              <Label color={getProgramRequestStatusColor(item.programRequestStatusId)}>
                {item.programRequestStatusName}
              </Label>
            </div>
          );
        },
        sortBy: ProgramRequestSort.ProgramRequestStatusInstitution
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item) => {
          return (<div className={"text-right"}>
            <ArrowButton ariaLabel={t("request.goToRequestDetails")}
              onClick={() => onOpenRequestDetails(item.programRequestId)}
            />
          </div>)
        }
      }
    ],
    rowToolTip: () => undefined
  };

  const onOpenRequestDetails = (requestId: number) => {
    history.push(programRequestDetailsUrl(requestId), { previousPage: PreviousPage.DashboardPage });
  };

  const handleUserInstitutionChange = (e: React.ChangeEvent<SelectEvent>) => {
    var institutionId = parseInt(e.target.value);
    setSelectedInstitution(institutionId);

    const changeUserActiveInstituttion = async () => {
      await getUserInstitutionSession(institutionId);

      window.location.reload();
    }
    changeUserActiveInstituttion();
  }

  var [tableProps, tableState, setFilters] = useTable<ProgramRequestInstitutionSearch>(
    {
      metadata: programRequestTableMetadata,
      data: programRequests.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: programRequests.rowCount,
      defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
    }
  )

  useEffect(() => {
    const checkIfUserHasProgramRequests = async () => {
      if (!userSession) {
        return;
      }
      var request = {
        programTypeIds: [],
        programName: '',
        programRequestStatusIds: [],
        dateFrom: undefined,
        dateTo: undefined,
        classificationMark: '',
        sortBy: ProgramRequestSort.ProgramRequestDate,
        sortDirection: SortDirection.Asc,
        page: 1,
        pageSize: 1
      };

      var results = await searchProgramRequestsInstitution(request);

      if (results.rowCount > 0) {
        setUserHasProgramRequests(true);
        let unlockedRequests = await getUnlockRequestCount(userSession?.institutionId!);

        if (unlockedRequests > 0 || results.pageCount === 1) {
          setUserHasUnlockaedRequests(unlockedRequests > 0);
          setUserHasUnlockaedInit(unlockedRequests > 0);
          if (!(!programRequestSearch || programRequestSearch.programRequestStatusIds.length === 0 || programRequestSearch.programRequestStatusIds.includes(2))) {
            setUserHasUnlockaedRequests(false);
          }
        }
      }
    }
    checkIfUserHasProgramRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstitution]);

  useEffect(() => {
    const getAndCheckInstitution = async () => {
      if (!userSession) {
        return;
      }

      var result = await getAndCheckInstitutionRequest(userSession.institutionId!);
      setInstitutionData(result);
    }
    getAndCheckInstitution();
  }, [userSession, selectedInstitution])

  if (institutionData && institutionData.oibServiceStatus === OIBServiceCheckStatus.IncompleteData) {
    history.push(institutionEditRoute());
  }

  useEffect(() => {
    const searchProgramRequests = async () => {
      var request = {
        ...programRequestSearch,
        sortBy: tableState.sortBy ?? ProgramRequestSort.ProgramRequestDate,
        sortDirection: tableState.order && tableState.order === ColumnSortEnum.ASC ? SortDirection.Asc : SortDirection.Desc,
        page: tableState.page ?? 1,
        pageSize: tableState.pageSize ?? 10
      };
      setLastUsedSearch(request);

      var results = await searchProgramRequestsInstitution(request);
      setProgramRequests(results);

      if (!request || request.programRequestStatusIds.length === 0 || request.programRequestStatusIds.includes(2)) {
        setUserHasUnlockaedRequests(userHasUnlockaedInit);
      }
      else {
        setUserHasUnlockaedRequests(false);
      }
    }
    searchProgramRequests();
    // programRequestSearch change shouldn't trigger this fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  useDidUpdateEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      ...programRequestSearch,
      sortBy: tableState.sortBy ?? ProgramRequestSort.ProgramRequestDate,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? 10
    }));
  }, [tableState])

  const onAddRequest = () => {
    history.push(programRequestNewRoute());
  };

  const clearFilters = () => {
    const emptySearch = {
      programTypeIds: [],
      programName: "",
      programRequestStatusIds: [],
      dateFrom: undefined,
      dateTo: undefined,
      classificationMark: ""
    };
    setProgramRequestSearch(emptySearch);
    setFilters(emptySearch);
  }

  const search = async () => {
    const isValid = doValidations();
    if (!isValid) {
      return;
    }
    setFilters(programRequestSearch);
  };

  const onCreateGroupProgramRequest = () => {
    history.push(createGroupProgramRequest());
  };

  const download = async (exportType: ExportTypeEnum) => {
    if (!lastUsedSearch) {
      return;
    }
    const request: ExportProgramRequestInstitutionRequest = {
      programTypeIds: lastUsedSearch.programTypeIds,
      programName: lastUsedSearch.programName,
      dateFrom: lastUsedSearch.dateFrom,
      dateTo: lastUsedSearch.dateTo,
      programRequestStatusIds: lastUsedSearch.programRequestStatusIds,
      exportType: exportType,
      sortBy: lastUsedSearch.sortBy,
      sortDirection: lastUsedSearch.sortDirection
    }
    exportProgramRequestInstitution(request);
  };

  const onCloseInfoPanel = () => {
    sessionStorage.setItem(infoStorageKey, "1");
  };

  return (
    <>

      {userInstitutions && userInstitutions.length > 1 &&
        <Row className="mt-5">
          <Col md={6}>
            <Select
              label={t("institution.selectActiveInstitution")}
              value={selectedInstitution}
              onChange={handleUserInstitutionChange}
              selectData={codeBookToSelectData(userInstitutions)}
            />
          </Col>
        </Row>
      }
      {/* {!infoPanelState &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("requests.mainInfoMessage")}
              color={"success"}
              fullWidth
              dismissible={true}
              onClose={ onCloseInfoPanel }
            />
          </Col>
        </Row>
      } */}
      {institutionData && (!institutionData.andragogicLeaders || institutionData.andragogicLeaders.length === 0) &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("common.andragogicLeadersNotCreated")}
              color={"danger"}
              fullWidth
            />
          </Col>
        </Row>

      }
      {userHasUnlockaedRequests &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("requests.unlockedRequestsExist")}
              color={"warning"}
              fullWidth
            />
          </Col>
        </Row>
      }
      {institutionData && institutionData.oibServiceStatus === OIBServiceCheckStatus.Unreachable &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("common.oibServiceUnreachable")}
              color={"danger"}
              fullWidth
            />
          </Col>
        </Row>
      }
      <Row className={styles.title}>
        <Col md={6}>
          <InstitutionBasicInfo institutionData={institutionData} />
        </Col>
        <Col md={6} className={styles.addCol}>
          <Button outline icon={<AddIcon />} onClick={onAddRequest} className={styles.actionButton}
            disabled={!institutionData || !institutionData.active ||
              (institutionData.oibServiceStatus !== OIBServiceCheckStatus.UpToDate && institutionData.oibServiceStatus !== OIBServiceCheckStatus.Unreachable)
              || (!institutionData.andragogicLeaders || institutionData.andragogicLeaders.length === 0)}>
            {t("common.addRequest")}
          </Button>
          <Button icon={<AddIcon />} outline onClick={onCreateGroupProgramRequest}
            disabled={!institutionData || !institutionData.active ||
              (institutionData.oibServiceStatus !== OIBServiceCheckStatus.UpToDate && institutionData.oibServiceStatus !== OIBServiceCheckStatus.Unreachable)
              || (!institutionData.andragogicLeaders || institutionData.andragogicLeaders.length === 0)}>
            {t("request.newGroupRequest")}
          </Button>
        </Col>
      </Row>

      {userHasProgramRequests &&
        <>
          <Row className="mt-5">
            <Col md={4}>
              <TextField
                label={t("common.programName")}
                name={nameof<typeof programRequestSearch>("programName")}
                value={programRequestSearch.programName}
                maxLength={50}
                onChange={handleChange(setProgramRequestSearch)}
              />
            </Col>
            <Col md={4}>
              <TextField
                label={t("common.classificationMark")}
                name={nameof<typeof programRequestSearch>("classificationMark")}
                value={programRequestSearch.classificationMark}
                maxLength={128}
                onChange={handleChange(setProgramRequestSearch)}
              />
            </Col>
            <Col md={4}>
              <Select
                multiple
                label={t("request.programType")}
                name={nameof<typeof programRequestSearch>("programTypeIds")}
                value={programRequestSearch.programTypeIds}
                onChange={handleChange(setProgramRequestSearch)}
                selectData={codeBookToSelectData(programTypes)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Select
                multiple
                label={t("common.programRequestStatus")}
                name={nameof<typeof programRequestSearch>("programRequestStatusIds")}
                value={programRequestSearch.programRequestStatusIds}
                onChange={handleChange(setProgramRequestSearch)}
                selectData={codeBookToSelectData(programRequestStatuses)}
              />
            </Col>
            <Col md={3}>
              <DatePicker
                value={programRequestSearch.dateFrom}
                label={t("common.dateFrom")}
                name={nameof<typeof programRequestSearch>("dateFrom")}
                onChange={(e) => {
                  if (e.target.value) {
                    let newDate = (e.target.value as Date);
                    newDate.setHours(0);
                    newDate.setMinutes(0);
                    newDate.setSeconds(0);
                    newDate.setMilliseconds(0);
                  }
                  handleChange(setProgramRequestSearch)(e);
                }}
                errorText={errors?.dateFrom}
                isClearable={true}
                minYear={new Date().getFullYear() - 25}
                maxYear={new Date().getFullYear() + 25}
              />
            </Col>
            <Col md={3}>
              <DatePicker
                value={programRequestSearch.dateTo}
                label={t("common.dateTo")}
                name={nameof<typeof programRequestSearch>("dateTo")}
                onChange={(e) => {
                  if (e.target.value) {
                    let newDate = (e.target.value as Date);
                    newDate.setHours(23);
                    newDate.setMinutes(59);
                    newDate.setSeconds(59);
                    newDate.setMilliseconds(0);
                  }
                  handleChange(setProgramRequestSearch)(e);
                }}
                isClearable={true}
                minYear={new Date().getFullYear() - 25}
                maxYear={new Date().getFullYear() + 25}
              />
            </Col>
          </Row>

          <Row className="mb-5 mt-5">
            <Col md={12} className="d-flex justify-content-end align-items-center">
              <Button onClick={search}>
                {t("common.search")}
              </Button>
              <Typography color="primary" className={styles.clearFilters + " ml-3"} onClick={clearFilters}>
                {t("common.clearFilters")}
              </Typography>
            </Col>
          </Row>
          <Table {...tableProps} />
          <Row className="mt-5">
            <Col md={12} className="d-flex justify-content-end align-items-center">
              <Button
                className={styles.exportButton}
                outline
                color={"primary"}
                icon={<UploadIcon />}
                onClick={() => download(ExportTypeEnum.Csv)}
              >
                {t("common.downloadDocumentExtension", { extension: ".csv" })}
              </Button>
              <Button
                className={styles.exportButton}
                outline
                color={"primary"}
                icon={<UploadIcon />}
                onClick={() => download(ExportTypeEnum.Excel)}
              >
                {t("common.downloadDocumentExtension", { extension: ".xslx" })}
              </Button>
            </Col>
          </Row>
        </>
      }

      {!userHasProgramRequests &&
        (<Row>
          <Col md={12} className={styles.emptyList}>
            <IconWrapper color="noted-2">
              <FolderAddIcon aria-label={t("folderAddIcon")} />
            </IconWrapper>
            <Typography variant="largeBold" component="h2">
              {t("requests.noRequests")}
            </Typography>
            <Typography>{t("requests.noRequestsInfo")}</Typography>
          </Col>
        </Row>)
      }
    </>
  );
}
